import { useCallback, useEffect, useState } from 'react';
import { pb } from './pb';
import { gateCurrentUser, type RosterMember } from './roster';

/* Auth state = a valid PocketBase session AND an active roster row.

   Both halves matter. A valid PB session on its own only proves the person has SOME CORE
   account; the roster row is what says they are on the Decked team. The two are resolved
   together so the rest of the app never has to think about the difference. */

export type AuthStatus = 'checking' | 'anonymous' | 'authenticated' | 'refused';

export interface AuthState {
  status: AuthStatus;
  member: RosterMember | null;
  /** Set when status is 'refused' — safe to show the user verbatim. */
  reason: string | null;
  /** True when the refusal was a failed check rather than a real denial, so "try again" helps. */
  transient: boolean;
  signOut: () => void;
  recheck: () => void;
}

export function useAuth(): AuthState {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [member, setMember] = useState<RosterMember | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [transient, setTransient] = useState(false);
  const [nonce, setNonce] = useState(0);

  const recheck = useCallback(() => setNonce((n) => n + 1), []);

  const signOut = useCallback(() => {
    pb.authStore.clear();
    setMember(null);
    setReason(null);
    setTransient(false);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!pb.authStore.isValid) {
        if (!cancelled) {
          setMember(null);
          setStatus('anonymous');
        }
        return;
      }

      if (!cancelled) setStatus('checking');
      const result = await gateCurrentUser();
      if (cancelled) return;

      if (result.ok && result.member) {
        setMember(result.member);
        setReason(null);
        setTransient(false);
        setStatus('authenticated');
        return;
      }

      /* Refused. Clear the PB session ONLY for a real denial — never for a transient lookup
         failure, or a PocketBase blip would silently sign out the whole team and they would
         all have to go back through Google. */
      setMember(null);
      setReason(result.reason || 'You do not have access to Real or Fake.');
      setTransient(Boolean(result.transient));
      if (!result.transient) pb.authStore.clear();
      setStatus('refused');
    }

    void resolve();
    const unsubscribe = pb.authStore.onChange(() => void resolve());
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [nonce]);

  return { status, member, reason, transient, signOut, recheck };
}
